import { BankAccountDetails, CashAppPayDetail } from '@afterpay/types'
import { EnrichedBankAccountDetails, EnrichedCardDetails } from './checkout'
import { ResultCode } from '@afterpaytouch/topaz-api'
import { PaymentMethodType } from '@afterpay/types'

export { PaymentMethodType }

export type TopazApiErrorMessage = 'topazPurchaseSetupError' | 'topazPaymentSetupError' | 'topazOrderSetupError'

export type PortalApiErrorMessage = 'portalApiCreditCardsInitiateNewError' | 'portalApiCreditCardsConfirmNewError' | 'portalApiCheckoutRetrieveCardsError'

export interface PortalApiError {
  message: PortalApiErrorMessage
  errorCode?: string
}
export interface TopazPaymentState {
  status: TopazStatus
  topazApiErrorMessage?: TopazApiErrorMessage
  portalApiError?: PortalApiError
  selected?: SelectedPaymentMethod
  threeDS?: ThreeDSState | undefined
}
export { ResultCode }
export enum TopazStatus {
  Initial,
  Saving,
  Saved,
  DuplicateCard,
  InvalidDetails,
  ExceededMaxAttemptsEnteringDetails,
  InvalidState,
  InvalidShippingDetails,
  UnacceptableCardIssuerRestricted,
  FailedToRetrieveNewCard,
  Error,
  TransientGatewayError,
  InsufficientFunds,
  UserChallengeFailed,
  UnacceptableCard,
  OtherError,
  InvalidRequestDetails,
  VelocityRuleBreach,
  FraudRuleBreach,
  RequireThreeDsAuthentication,
  ThreeDSChallengePending,
  CashAppCardIneligible,
}

export enum ApplePayStatus {
  Initial,
  Cancelled,
  PaymentAuthorized,
  SessionInitialized,
  Error,
}

export enum ApplePayError {
  SessionInitializationFailed,
  BeginningSessionFailed,
  InvalidCardType,
  MerchantValidationFailed,
  PaymentRejected,
  UnknownError,
  BinBlocked,
}

export interface PaymentSourceCaptureErrorBody {
  code: string
  message: string
}

export interface PaymentSourceCaptureError extends PaymentSourceCaptureErrorBody {
  code: string
  message: string
  errors: PaymentSourceCaptureError[]
}

export interface PaymentSourceCaptureErrorResponse {
  error: PaymentSourceCaptureError
}

export type SelectedPaymentMethod =
  | {
      type: PaymentMethodType.CREDIT_CARD
      card: SavedCard | NewCard
    }
  | {
      type: PaymentMethodType.ACH_BANK_ACCOUNT
      account: BankAccountDetails
    }
  | {
      type: PaymentMethodType.APPLE_PAY
    }
  | {
      type: PaymentMethodType.CASH_APP_PAY
      cashAppPay: CashAppPayDetail
    }

export type PaymentMechanism = NewCard | SavedCard | ApplePayToken

export const isSavedCard = (paymentMechanism: PaymentMechanism): paymentMechanism is SavedCard => 'token' in paymentMechanism
export const isNewCard = (paymentMechanism: PaymentMechanism): paymentMechanism is NewCard => 'cardNumber' in paymentMechanism
export const isApplePayToken = (paymentMechanism: PaymentMechanism): paymentMechanism is ApplePayToken => 'paymentSourceId' in paymentMechanism

export function isSavedCardValid(savedCard: SavedCard, savedCards: EnrichedCardDetails[]): boolean {
  const details = savedCards.find((card) => card.token === savedCard.token)
  if (details !== undefined) {
    return details.error === null
  } else {
    return false
  }
}

export function isSavedBankAccountValid(bankAccount: BankAccountDetails, savedBankAccounts: EnrichedBankAccountDetails[] | undefined): boolean {
  const details = savedBankAccounts?.find((account) => account.id === bankAccount.id)
  if (details !== undefined) {
    return details.error === null
  } else {
    return false
  }
}

export type TopazSetupRequest = PaymentMechanism & { traceId: string }

export interface NewCard {
  cardHolderName: string
  cardNumber: string
  cardSecurityCode: string
  cardExpiryMonth: string
  cardExpiryYear: string
  creditCardType?: string
  address?: {
    street1?: string
    street2?: string
    city?: string
    state?: string
    postcode?: string
    country?: string
  }
}

export interface SavedCard {
  token: string
}

export interface ApplePayToken {
  paymentSourceId: string
}

export interface PaymentSourceCaptureMPANFlowRequest {
  user_id: string
  transaction_reference: {
    id: string
  }
  apple_pay: PaymentSource
  stored?: boolean
}

export interface PaymentSource {
  token: string
  billing_address: PaymentSourceBillingAddress
  payment_network: 'MasterCard' | 'Visa' | 'Discover'
  payment_method_type: 'debit' | 'credit'
  payment_method_display_name: string
  transaction_identifier: string
}

export interface PaymentSourceBillingAddress {
  address_line_1: string
  address_line_2?: string
  city: string
  country: string
  postal_code: string
  state?: string
}

export interface MPANPaymentSourceCaptureResponse {
  payment_source_id: string
  card_type: MPANPaymentSourceCaptureCardTypes
  disable_bin_blocking?: boolean
}

export enum PaymentSourceCaptureCardTypes {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT',
  PREPAID = 'PREPAID',
  AFTERPAY = 'AFTERPAY',
}

export enum MPANPaymentSourceCaptureCardTypes {
  DEBIT = 'debit',
  CREDIT = 'credit',
  PREPAID = 'prepaid',
  AFTERPAY = 'afterpay',
}

export interface ThreeDSState {
  status: ThreeDSecureStatus
  challenge?: ThreeDSChallengeState
  threeDSId?: string
  error?: InitiateThreeDSErrorCode
}

export enum ThreeDSecureStatus {
  Initiating,
  Initiated,
  Finalising,
  Finalised,
  Error,
}

export interface ThreeDSChallengeState {
  status: ThreeDSChallengeStatus
  challengeUri?: string
  error?: FinaliseThreeDSErrorCode
}

export enum ThreeDSChallengeStatus {
  Pending,
  ChallengeNotRequired,
  ChallengeRequired,
}

export enum ThreeDSWorkflows {
  CHECKOUT = 'CHECKOUT',
  PAYNOW = 'PAYNOW',
  BILLING = 'BILLING',
}

export interface InitiateThreeDSRequest {
  traceID: string
  saveCard: boolean
  workflow: ThreeDSWorkflows
}

export interface InitiateThreeDSResponse {
  threeDSId: string
  challengeRequired: boolean
  challengeUri: string | null
}

export enum InitiateThreeDSErrorCode {
  PAYMENT_REQUIRED = 402,
  PRECONDITION_FAILED = 412,
  INTERNAL_SERVER_ERROR = 500,
}

export interface FinaliseThreeDSRequest {
  traceID: string
  threeDSId: string
  workflow: ThreeDSWorkflows
}

export enum FinaliseThreeDSErrorCode {
  USER_CHALLENGE_FAILED = 402,
}

export enum PaymentCaptureType {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}
